import _extends from "@babel/runtime/helpers/extends";
import _taggedTemplateLiteral from "@babel/runtime/helpers/taggedTemplateLiteral";

var _templateObject, _templateObject2, _templateObject3;

import React from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { Fonts } from '@vds-core/typography';
import { withVDSManager, calculateRem } from '@vds-core/utilities';
import { AccessibilityTokens } from '@vds-tokens/accessibility';
import { ColorTokens } from '@vds-tokens/color';
import { TypographyTokens } from '@vds-tokens/typography'; // Style properties

var colorFocusRing = AccessibilityTokens.color.focusring.onlight.value;
var colorFocusRingInverted = AccessibilityTokens.color.focusring.ondark.value;
var fontWeightRegular = TypographyTokens.fontweight.regular.value;
var fontWeightBold = TypographyTokens.fontweight.bold.value;
var lineHeightDesktopLarge = TypographyTokens.lineheight.body[20].value;
var heightHitAreaStandalone = calculateRem(44);
var widthFocusRing = AccessibilityTokens.focusring.borderwidth.value;
var offsetFocusRing = AccessibilityTokens.focusring.space.offset.value;
var transitionDuration = '0.15s';

var focusRingStyle = function focusRingStyle() {
  var mode = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 'default';
  var color = mode === 'inverted' ? colorFocusRingInverted : colorFocusRing;
  return "".concat(calculateRem(widthFocusRing), " ").concat(AccessibilityTokens.focusring.borderstyle.value, " ").concat(color);
};

var focusRingSize = "calc(100% + ".concat(calculateRem((parseInt(offsetFocusRing) + parseInt(widthFocusRing)) * 2), ")");
var focusRingPosition = "calc(50% + ".concat(calculateRem(widthFocusRing), ")");
var propTypes = {
  /**
   * Determines display layout of button.
   */
  type: PropTypes.oneOf(['inline', 'standAlone']),

  /**
   * @ignore
   * Determines viewport of button.
   */
  viewport: PropTypes.oneOf(['mobile', 'tablet', 'desktop']),

  /**
   * String that is used to render as the text link.
   */
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.string]),

  /**
   * TextLink will be disabled if disabled prop is passed.
   */
  disabled: PropTypes.bool,

  /**
   *	This function will be called when the TextLink is clicked. It will return an event.
   */
  onClick: PropTypes.func,

  /**
   *	This function will be called when keydown event fires on TextLink. It will return an event.
   */
  onKeyDown: PropTypes.func,

  /**
   * This string will be applied to the href attribute.
   */
  href: PropTypes.string,

  /**
   * @ignore
   * If true, TextLink text will render as bold.
   */
  bold: PropTypes.bool,

  /**
   *	If provided, used as string that labels the element for accessibility.
   */
  ariaLabel: PropTypes.string,

  /**
   * If provided, will render the TextLink in corresponding sizes
   */
  size: PropTypes.oneOf(['small', 'large']),

  /**
   * @ignore
   */
  calculateColors: PropTypes.func,

  /**
   * @ignore
   */
  calculateUnderLine: PropTypes.func,

  /**
   * @ignore
   */
  calculateLetterSpacing: PropTypes.func,

  /**
   * Allows a string to be provided for analytics.
   */
  'data-analyticstrack': PropTypes.string,

  /**
   * Allows a string to be provided for analytics.
   */
  'data-track': PropTypes.string,

  /**
   * Allows a string to be provided for ignoring analytics tagging.
   */
  'data-track-ignore': PropTypes.string,

  /**
   * Allows a string to be provided for click stream.
   */
  'data-clickstream': PropTypes.string,

  /**
   * Allows a string to be provided for analytics tagging.
   */
  'data-sitecat-level': PropTypes.string,

  /**
   * Allows a string to be provided for analytics tagging.
   */
  'data-sitecat-position': PropTypes.string,

  /**
   * Allows a string to be provided for analytics tagging.
   */
  'data-sitecat-datatrack': PropTypes.string,

  /**
   * Allows a string to be provided for analytics tagging.
   */
  'data-sitecat-cta': PropTypes.string,

  /**
   * @ignore
   */
  dataLoc: PropTypes.string,

  /**
   * Allows a unique ID to be passed to the component.
   */
  id: PropTypes.string,

  /**
   * @ignore
   */
  tabIndex: PropTypes.number,

  /**
   * This will be applied to the role attribute for screen reading capabilities
   */
  role: PropTypes.oneOf(['link', 'button']),

  /**
   * @ignore
   */
  focusRingBorderRadius: PropTypes.string,

  /**
   * @ignore
   */
  surface: PropTypes.oneOf(['light', 'dark'])
};

var _calculateColors = function _calculateColors(surface) {
  return {
    "default": surface === 'dark' ? ColorTokens.elements.primary.ondark.value : ColorTokens.elements.primary.onlight.value,
    hover: surface === 'dark' ? ColorTokens.elements.primary.ondark.value : ColorTokens.elements.primary.onlight.value,
    active: surface === 'dark' ? ColorTokens.interactive.active.ondark.value : ColorTokens.interactive.active.onlight.value,
    disabled: surface === 'dark' ? ColorTokens.interactive.disabled.ondark.value : ColorTokens.interactive.disabled.onlight.value
  };
};

var _calculateUnderLine = function _calculateUnderLine(calculateColors, surface) {
  var color = calculateColors(surface);
  return {
    "default": "".concat(calculateRem(1), " solid ").concat(color["default"]),
    hover: "".concat(calculateRem(2), " solid ").concat(color.hover),
    active: "".concat(calculateRem(2), " solid ").concat(color.active),
    disabled: "".concat(calculateRem(1), " solid ").concat(color.disabled)
  };
};

var _calculateLetterSpacing = function _calculateLetterSpacing(size, viewport, bold) {
  return size === 'small' ? '0px' : TypographyTokens.letterspacing.wide.value;
};

var defaultProps = {
  size: 'large',
  viewport: 'desktop',
  type: 'inline',
  disabled: false,
  surface: 'light',
  onClick: function onClick() {},
  bold: false,
  tabIndex: 0,
  role: 'link',
  calculateLetterSpacing: _calculateLetterSpacing,
  calculateColors: _calculateColors,
  calculateUnderLine: _calculateUnderLine,
  focusRingBorderRadius: '2px'
};
var HitArea = /*#__PURE__*/styled.span.withConfig({
  displayName: "HitArea",
  componentId: "VDS__sc-9x52p1-0"
})(_templateObject || (_templateObject = _taggedTemplateLiteral(["\n  box-sizing: content-box;\n  cursor: pointer;\n  display: inline;\n  height: ", ";\n  left: 50%;\n  outline: none;\n  position: absolute;\n  text-align: center;\n  top: 50%;\n  transform: translate(-50%, -50%);\n  width: 100%;\n\n  &:hover {\n    cursor: pointer;\n  }\n"])), function (_ref) {
  var type = _ref.type;
  return type === 'inline' ? '1.2em' : heightHitAreaStandalone;
});
var Wrapper = /*#__PURE__*/styled.span.withConfig({
  displayName: "Wrapper",
  componentId: "VDS__sc-9x52p1-1"
})(_templateObject2 || (_templateObject2 = _taggedTemplateLiteral(["\n  height: ", ";\n  width: 100%;\n  position: relative;\n\n  &:focus {\n    outline: none;\n  }\n"])), function (_ref2) {
  var type = _ref2.type;
  return type === 'inline' ? 'inherit' : calculateRem(lineHeightDesktopLarge);
});
var StyledAnchor = /*#__PURE__*/styled.a.withConfig({
  displayName: "StyledAnchor",
  componentId: "VDS__sc-9x52p1-2"
})(_templateObject3 || (_templateObject3 = _taggedTemplateLiteral(["\n  background-image: none;\n  place-self: baseline;\n  background-color: transparent;\n  border: 0;\n  border-bottom: ", ";\n  box-sizing: border-box;\n  color: ", ";\n  cursor: pointer;\n  outline: none;\n  position: relative;\n  text-decoration: none;\n  touch-action: manipulation;\n  transition: opacity ", " ease-in;\n  white-space: nowrap;\n  width: auto;\n\n  ", "\n\n  ", "\n  outline: none;\n  &:focus:not(:hover) {\n    &::after {\n      box-sizing: border-box;\n      border: ", ";\n      border-radius: ", ";\n      content: '';\n      left: 50%;\n      position: absolute;\n      top: ", ";\n      transform: translate(-50%, -50%);\n      height: ", ";\n      width: ", ";\n    }\n  }\n  &:focus:not(:hover):not(:focus-visible) {\n    &:after {\n      display: none; // Only display focus state on keyboard focus, not when radio button is clicked\n    }\n  }\n  &:hover {\n    color: ", ";\n    border-bottom: ", ";\n  }\n\n  &:active {\n    color: ", ";\n    border-bottom: ", ";\n  }\n\n  ", ";\n\n  ", ";\n"])), function (_ref3) {
  var underline = _ref3.underline;
  return underline["default"];
}, function (_ref4) {
  var colors = _ref4.colors;
  return colors["default"];
}, transitionDuration, function (_ref5) {
  var type = _ref5.type;
  return type === 'inline' && "\n    font-size: inherit;\n    font-family: inherit;\n    font-weight: inherit;\n    line-height: inherit\n    letter-spacing: inherit;\n  ";
}, function (_ref6) {
  var type = _ref6.type,
      bold = _ref6.bold,
      fontStyles = _ref6.fontStyles,
      size = _ref6.size;
  return type !== 'inline' && "\n    font-size: ".concat(calculateRem(fontStyles.fontSize), ";\n    font-family: ").concat(size === 'large' ? Fonts.VerizonNHGeDS : Fonts.VerizonNHGeTX, ";\n    font-weight: ").concat(bold ? fontWeightBold : fontWeightRegular, ";\n    line-height: ").concat(calculateRem(fontStyles.lineHeight), "\n    letter-spacing: ").concat(calculateRem(fontStyles.letterSpacing), ";\n  ");
}, focusRingStyle('default'), function (_ref7) {
  var focusRingBorderRadius = _ref7.focusRingBorderRadius;
  return focusRingBorderRadius;
}, focusRingPosition, focusRingSize, focusRingSize, function (_ref8) {
  var colors = _ref8.colors;
  return colors.hover;
}, function (_ref9) {
  var underline = _ref9.underline;
  return underline.hover;
}, function (_ref10) {
  var colors = _ref10.colors;
  return colors.active;
}, function (_ref11) {
  var underline = _ref11.underline;
  return underline.active;
}, function (_ref12) {
  var disabled = _ref12.disabled,
      colors = _ref12.colors,
      underline = _ref12.underline;
  return disabled && "\n    border-bottom: ".concat(underline.disabled, ";\n    color: ").concat(colors.disabled, ";\n    pointer-events: none;\n  ");
}, function (_ref13) {
  var surface = _ref13.surface;
  return surface === 'dark' && "\n    &:focus:not(:hover) {\n      &::after {\n        border: ".concat(focusRingStyle('inverted'), ";\n      }\n    }\n  ");
});

var _getFontSize = function _getFontSize(size) {
  switch (size) {
    case 'small':
      return TypographyTokens.fontsize.body[12].value;

    case 'large':
    default:
      return TypographyTokens.fontsize.body[16].value;
  }
};

var _getLineHeight = function _getLineHeight(size) {
  switch (size) {
    case 'small':
      return TypographyTokens.lineheight.body[16].value;

    case 'large':
    default:
      return TypographyTokens.lineheight.body[20].value;
  }
};

var getFontStyles = function getFontStyles(type, viewport, size, bold) {
  var inline = type === 'inline';
  return {
    fontSize: inline ? 'inherit' : _getFontSize(size),
    lineHeight: inline ? 'inherit' : _getLineHeight(size),
    letterSpacing: inline ? 'inherit' : _calculateLetterSpacing(size, viewport, bold)
  };
};

var TextLink = function TextLink(props) {
  var id = props.id,
      disabled = props.disabled,
      dataLoc = props.dataLoc,
      tabIndex = props.tabIndex,
      role = props.role,
      calculateColors = props.calculateColors,
      calculateUnderLine = props.calculateUnderLine,
      surface = props.surface,
      viewport = props.viewport,
      focusRingBorderRadius = props.focusRingBorderRadius,
      onClick = props.onClick;
  var newAriaLabel = props.ariaLabel ? props.ariaLabel : props.children && typeof props.children === 'string' ? props.children : 'Text Link';
  var colors = calculateColors(surface);
  var underline = calculateUnderLine(calculateColors, surface);
  var viewportToUse = viewport === 'tablet' ? 'desktop' : viewport; // normalize tablet to desktop

  var handleKeyDown = function handleKeyDown(e) {
    props.onKeyDown && props.onKeyDown(e);

    if (e.key === 'Enter' || e.key === ' ') {
      props.onClick && props.onClick(e);
    }
  };

  var _onClick = function _onClick(e) {
    //reassign the target due to the hit area propogation
    var newEvent = e;
    newEvent.target = e.currentTarget;
    if (onClick) return onClick(newEvent);
  };

  return /*#__PURE__*/React.createElement(StyledAnchor, _extends({}, props, {
    onClick: _onClick,
    id: id,
    type: props.type,
    disabled: disabled,
    surface: surface,
    href: props.href,
    bold: props.bold,
    "aria-label": newAriaLabel,
    role: role,
    "aria-disabled": props.disabled,
    tabIndex: props.disabled ? -1 : tabIndex ? tabIndex : 0,
    "data-loc": dataLoc,
    fontStyles: getFontStyles(props.type, viewportToUse, props.size, props.bold),
    colors: colors,
    underline: underline,
    focusRingBorderRadius: focusRingBorderRadius,
    onKeyDown: handleKeyDown
  }), /*#__PURE__*/React.createElement(HitArea, {
    type: props.type,
    tabIndex: -1,
    "aria-hidden": true
  }), /*#__PURE__*/React.createElement(Wrapper, {
    tabIndex: -1,
    "aria-hidden": true,
    type: props.type
  }, props.children));
};

TextLink.defaultProps = defaultProps;
TextLink.propTypes = propTypes;
export default withVDSManager(TextLink);